/**
 * Training Hours Service
 *
 * Centralized service for fetching and aggregating training hours data.
 * Used by R0006 to subtract training hours from scheduled hours in the
 * "Sched vs Model" calculation.
 *
 * @module trainingHoursService
 *
 * FUTURE-PROOFING NOTES:
 * - Date matching: Currently uses weekOf-based storage with day of week.
 *   TODO: Update when training DB migrates to date-based storage.
 * - Store matching: Currently matches by store shortName.
 *   TODO: Update when training DB migrates to locationId-based storage.
 */

import { API } from 'aws-amplify';
import { getReport } from '../graphql/queries';
import { format, startOfWeek, parseISO, addDays } from 'date-fns';
import * as logger from '../utils/logger';

/**
 * Gets the Monday (week start) for a given date.
 *
 * @param {Date|string} date - Target date
 * @returns {string} Week start date in 'yyyy-MM-dd' format
 *
 * @example
 * getWeekStartDate(new Date(2026, 2, 5)); // Returns '2026-03-02' (Monday)
 */
export const getWeekStartDate = (date) => {
  const targetDate = typeof date === 'string' ? parseISO(date) : date;
  // Week starts on Monday (weekStartsOn: 1)
  const monday = startOfWeek(targetDate, { weekStartsOn: 1 });
  return format(monday, 'yyyy-MM-dd');
};

/**
 * Gets the day name for a given date.
 *
 * @param {Date|string} date - Target date
 * @returns {string} Day name (e.g., 'Monday', 'Tuesday')
 *
 * @example
 * getDayName(new Date(2026, 2, 2)); // Returns 'Monday'
 */
export const getDayName = (date) => {
  const targetDate = typeof date === 'string' ? parseISO(date) : date;
  return format(targetDate, 'EEEE');
};

/**
 * Aggregates training hours by store and type (FOH/BOH) for a specific day.
 *
 * @param {Array|null} trainingData - Raw training data from API
 * @param {string} targetDay - Day name to filter by (e.g., 'Monday')
 * @returns {Object} Aggregated hours by store: { [storeName]: { foh: number, boh: number } }
 *
 * @example
 * const result = aggregateTrainingHours(data, 'Monday');
 * // Returns: { 'Store A': { foh: 3, boh: 2.5 }, 'Store B': { foh: 0, boh: 1 } }
 */
export const aggregateTrainingHours = (trainingData, targetDay) => {
  if (!trainingData || !Array.isArray(trainingData)) {
    return {};
  }

  const result = {};

  for (const storeData of trainingData) {
    const storeName = storeData.store;

    // TODO: Update store matching when DB migrates to locationId
    // Current: match by shortName (storeName)
    // Future: match by locationId
    // const storeKey = storeData.locationId || storeData.store;

    // Initialize store with zero hours
    result[storeName] = { foh: 0, boh: 0 };

    if (!storeData.data || !Array.isArray(storeData.data)) {
      continue;
    }

    // Find the matching day
    const dayData = storeData.data.find((d) => d.day === targetDay);

    if (!dayData || !dayData.hours || !Array.isArray(dayData.hours)) {
      continue;
    }

    // Aggregate hours by type
    for (const record of dayData.hours) {
      const hours = Number(record.hours) || 0;

      if (record.type === 'BOH') {
        result[storeName].boh += hours;
      } else if (record.type === 'FOH') {
        result[storeName].foh += hours;
      }
    }
  }

  return result;
};

/**
 * Fetches training hours for a specific date and aggregates by store and type.
 *
 * @param {Object} params - Fetch parameters
 * @param {Object} params.user - User object with userName and client properties
 * @param {Date|string} params.date - Target date for training data
 * @returns {Promise<Object>} Training hours by store: { [storeName]: { foh: number, boh: number } }
 *
 * @example
 * const trainingHours = await fetchTrainingHours({
 *   user: { userName: 'john', client: 'grm' },
 *   date: new Date(2026, 2, 2)
 * });
 * // Returns: { 'Store A': { foh: 3, boh: 2 }, 'Store B': { foh: 0, boh: 0 } }
 */
export const fetchTrainingHours = async ({ user, date }) => {
  try {
    // TODO: Update date handling when training DB migrates to date-based storage
    // Current: Calculate weekOf and use day name
    // Future: Use date directly
    const weekOf = getWeekStartDate(date);
    const dayName = getDayName(date);

    const reportVariables = [
      {
        name: 'arguments',
        value: JSON.stringify({
          weekOf,
          stores: ['all']
        })
      }
    ];

    const response = await API.graphql({
      query: getReport,
      variables: {
        userName: user.userName,
        clientId: user.client,
        reportId: 'trainingHours',
        reportVariables
      },
      authMode: 'AMAZON_COGNITO_USER_POOLS'
    });

    // Parse the response
    const reportData = response?.data?.getReport;

    if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
      logger.debug('No training data found', { weekOf, dayName });
      return {};
    }

    const rowData = reportData[0]?.data;

    if (!rowData || !Array.isArray(rowData) || rowData.length === 0) {
      logger.debug('Empty training data response', { weekOf, dayName });
      return {};
    }

    const cellValue = rowData[0]?.rowCells?.[0]?.value;

    if (!cellValue) {
      logger.debug('No cell value in training response', { weekOf, dayName });
      return {};
    }

    const trainingData = JSON.parse(cellValue);

    const aggregatedHours = aggregateTrainingHours(trainingData, dayName);

    logger.debug('Successfully fetched training hours', {
      weekOf,
      dayName,
      storeCount: Object.keys(aggregatedHours).length
    });

    return aggregatedHours;
  } catch (error) {
    logger.error('Failed to fetch training hours', {
      error: error.message,
      date: typeof date === 'string' ? date : date?.toISOString?.()
    });
    return {};
  }
};

/**
 * Gets training hours for a specific store from aggregated data.
 * Returns default values (0) if store not found.
 *
 * @param {Object} aggregatedData - Aggregated training hours from fetchTrainingHours
 * @param {string} storeName - Store name to look up
 * @returns {Object} Training hours: { foh: number, boh: number }
 *
 * @example
 * const storeHours = getStoreTrainingHours(aggregatedData, 'Store A');
 * // Returns: { foh: 3, boh: 2 } or { foh: 0, boh: 0 } if not found
 */
export const getStoreTrainingHours = (aggregatedData, storeName) => {
  // TODO: Update store lookup when DB migrates to locationId
  // Current: lookup by shortName
  // Future: lookup by locationId
  return aggregatedData?.[storeName] || { foh: 0, boh: 0 };
};

/**
 * Fetches training hours for a week and structures by store and day.
 * Returns data in a format suitable for the labor hours transform function.
 *
 * @param {Object} params - Fetch parameters
 * @param {Object} params.user - User object with userName and client properties
 * @param {string} params.weekOf - Week start date in 'yyyy-MM-dd' format
 * @returns {Promise<Object>} Training hours by store and day:
 *   { [storeName]: { [dayName]: { foh: number, boh: number } } }
 *
 * @example
 * const trainingByStoreDay = await fetchTrainingHoursForWeek({
 *   user: { userName: 'john', client: 'grm' },
 *   weekOf: '2026-03-02'
 * });
 * // Returns: { 'Store A': { 'Monday': { foh: 3, boh: 2 }, 'Tuesday': { foh: 0, boh: 1 } } }
 */
export const fetchTrainingHoursForWeek = async ({ user, weekOf }) => {
  try {
    const reportVariables = [
      {
        name: 'arguments',
        value: JSON.stringify({
          weekOf,
          stores: ['all']
        })
      }
    ];

    const response = await API.graphql({
      query: getReport,
      variables: {
        userName: user.userName,
        clientId: user.client,
        reportId: 'trainingHours',
        reportVariables
      },
      authMode: 'AMAZON_COGNITO_USER_POOLS'
    });

    // Parse the response
    const reportData = response?.data?.getReport;

    if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
      logger.debug('No training data found for week', { weekOf });
      return {};
    }

    const rowData = reportData[0]?.data;

    if (!rowData || !Array.isArray(rowData) || rowData.length === 0) {
      logger.debug('Empty training data response for week', { weekOf });
      return {};
    }

    const cellValue = rowData[0]?.rowCells?.[0]?.value;

    if (!cellValue) {
      logger.debug('No cell value in training response for week', { weekOf });
      return {};
    }

    const trainingData = JSON.parse(cellValue);

    // Structure by store and day
    const result = {};

    if (!trainingData || !Array.isArray(trainingData)) {
      return {};
    }

    for (const storeData of trainingData) {
      const storeName = storeData.store;

      // TODO: Update store matching when DB migrates to locationId
      // Current: match by shortName (storeName)
      // Future: match by locationId

      if (!result[storeName]) {
        result[storeName] = {};
      }

      if (!storeData.data || !Array.isArray(storeData.data)) {
        continue;
      }

      // Process each day's training data
      for (const dayData of storeData.data) {
        const dayName = dayData.day;

        if (!dayName || !dayData.hours || !Array.isArray(dayData.hours)) {
          continue;
        }

        // Aggregate hours by type for this day
        let foh = 0;
        let boh = 0;

        for (const record of dayData.hours) {
          const hours = Number(record.hours) || 0;

          if (record.type === 'BOH') {
            boh += hours;
          } else if (record.type === 'FOH') {
            foh += hours;
          }
        }

        result[storeName][dayName] = { foh, boh };
      }
    }

    logger.debug('Successfully fetched training hours for week', {
      weekOf,
      storeCount: Object.keys(result).length
    });

    return result;
  } catch (error) {
    logger.error('Failed to fetch training hours for week', {
      error: error.message,
      weekOf
    });
    return {};
  }
};

/**
 * Returns every Monday-start ISO week (in 'yyyy-MM-dd' format) that overlaps
 * the given date range, ordered earliest-first. Used to fan out per-week
 * training-hours fetches for a multi-week range.
 *
 * @param {Date|string} startDate - Range start (inclusive)
 * @param {Date|string} endDate - Range end (inclusive)
 * @returns {string[]} Week start dates (Mondays)
 *
 * @example
 * getWeekStartsInRange('2026-04-01', '2026-04-26');
 * // Returns ['2026-03-30', '2026-04-06', '2026-04-13', '2026-04-20']
 */
export const getWeekStartsInRange = (startDate, endDate) => {
  const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;
  const weekStarts = [];
  let cursor = startOfWeek(start, { weekStartsOn: 1 });
  while (cursor <= end) {
    weekStarts.push(format(cursor, 'yyyy-MM-dd'));
    cursor = addDays(cursor, 7);
  }
  return weekStarts;
};

/**
 * Fetches training hours for every ISO week that overlaps the given range and
 * returns a week-keyed map suitable for the labor-hours transform.
 *
 * Each week is fetched in parallel via `fetchTrainingHoursForWeek`. A week
 * whose fetch fails resolves to `{}` (non-fatal) so downstream lookups
 * default to zero training for that week's days.
 *
 * @param {Object} params
 * @param {Object} params.user - Authenticated user; `userName` and `client` are sent to the trainingHours report.
 * @param {Date|string} params.startDate - Range start (inclusive)
 * @param {Date|string} params.endDate - Range end (inclusive)
 * @returns {Promise<Object>} Map shaped as
 *   `{ [weekStart]: { [storeName]: { [dayName]: { foh: number, boh: number } } } }`
 *
 * @example
 * const trainingByWeek = await fetchTrainingHoursForRange({
 *   user: { userName: 'jane', client: 'grm' },
 *   startDate: '2026-04-01',
 *   endDate: '2026-04-26'
 * });
 * // trainingByWeek['2026-04-13']['First Colony']['Monday'] -> { foh, boh }
 */
export const fetchTrainingHoursForRange = async ({ user, startDate, endDate }) => {
  try {
    const weekStarts = getWeekStartsInRange(startDate, endDate);

    const perWeek = await Promise.all(
      weekStarts.map((weekOf) =>
        fetchTrainingHoursForWeek({ user, weekOf }).then((data) => [weekOf, data || {}])
      )
    );

    const result = {};
    for (const [weekOf, data] of perWeek) {
      result[weekOf] = data;
    }

    logger.debug('Successfully fetched training hours for range', {
      startDate,
      endDate,
      weekCount: weekStarts.length
    });

    return result;
  } catch (error) {
    logger.error('Failed to fetch training hours for range', {
      error: error.message,
      startDate,
      endDate
    });
    return {};
  }
};
